import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const MINI_WIDTH = 280;
const MINI_HEIGHT = 180;
const MARGIN = { top: 28, right: 16, bottom: 32, left: 44 };

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
    flexWrap: 'wrap',
    gap: '8px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
  },
  buttonGroup: {
    display: 'flex',
    gap: '0px',
  },
  button: {
    padding: '4px 12px',
    fontSize: '11px',
    fontWeight: 600,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    border: '1px solid #d1d5db',
    cursor: 'pointer',
    outline: 'none',
    transition: 'background 0.15s, color 0.15s',
  },
  buttonLeft: {
    borderRadius: '6px 0 0 6px',
    borderRight: 'none',
  },
  buttonRight: {
    borderRadius: '0 6px 6px 0',
  },
  buttonActive: {
    background: '#2563eb',
    color: '#fff',
    borderColor: '#2563eb',
  },
  buttonInactive: {
    background: '#fff',
    color: '#6b7280',
    borderColor: '#d1d5db',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
  },
  cell: {
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '0',
    background: '#fff',
    overflow: 'hidden',
  },
  empty: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
    fontSize: '14px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '260px',
  },
  tooltipTitle: {
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  tooltipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    color: '#6b7280',
    fontSize: '11px',
    lineHeight: '1.6',
  },
  impulseLabel: {
    display: 'inline-block',
    background: '#eff6ff',
    color: '#2563eb',
    fontSize: '11px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '4px',
    marginBottom: '12px',
  },
};

const FIELD_LABELS = {
  pest365: 'Pest365',
  pest365Plus: 'Pest365+',
  initialServiceVisit: 'Initial Visit',
  oneTime: 'One-Time',
  mosquitoTickControl: 'Mosquito/Tick',
};

function fieldLabel(field) {
  return FIELD_LABELS[field] || field;
}

export default function ImpulseResponseChart({ data }) {
  const svgRefs = useRef({});
  const [viewMode, setViewMode] = useState('impulse');
  const [tooltip, setTooltip] = useState(null);

  const responses = useMemo(() => {
    if (!data || !data.responses || data.responses.length === 0) return [];
    return data.responses;
  }, [data]);

  useEffect(() => {
    if (!responses || responses.length === 0) return;

    responses.forEach((resp) => {
      const svgEl = svgRefs.current[resp.responseField];
      if (!svgEl) return;

      const svg = d3.select(svgEl);
      svg.selectAll('*').remove();

      const innerW = MINI_WIDTH - MARGIN.left - MARGIN.right;
      const innerH = MINI_HEIGHT - MARGIN.top - MARGIN.bottom;

      svg.attr('viewBox', `0 0 ${MINI_WIDTH} ${MINI_HEIGHT}`);

      const g = svg.append('g')
        .attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

      const periods = resp.periods || [];
      const values = viewMode === 'cumulative' ? (resp.cumulative || []) : (resp.response || []);
      const upper = resp.upperBound || [];
      const lower = resp.lowerBound || [];

      if (periods.length === 0 || values.length === 0) return;

      // Use cumulative bounds if in cumulative mode — approximate from response bounds
      const displayUpper = viewMode === 'cumulative'
        ? values.map((v, i) => v + ((upper[i] || 0) - (resp.response[i] || 0)))
        : upper;
      const displayLower = viewMode === 'cumulative'
        ? values.map((v, i) => v + ((lower[i] || 0) - (resp.response[i] || 0)))
        : lower;

      // Scales
      const xScale = d3.scaleLinear()
        .domain(d3.extent(periods))
        .range([0, innerW]);

      const allY = [...values, ...displayUpper, ...displayLower];
      const yMin = d3.min(allY);
      const yMax = d3.max(allY);
      const yPad = (yMax - yMin) * 0.15 || 0.1;

      const yScale = d3.scaleLinear()
        .domain([yMin - yPad, yMax + yPad])
        .nice()
        .range([innerH, 0]);

      // Grid lines
      const yTicks = yScale.ticks(4);
      g.selectAll('.grid-line')
        .data(yTicks)
        .enter()
        .append('line')
        .attr('x1', 0)
        .attr('x2', innerW)
        .attr('y1', (d) => yScale(d))
        .attr('y2', (d) => yScale(d))
        .attr('stroke', '#f3f4f6')
        .attr('stroke-width', 1);

      // Zero line (dashed)
      const zeroY = yScale(0);
      if (zeroY >= 0 && zeroY <= innerH) {
        g.append('line')
          .attr('x1', 0)
          .attr('x2', innerW)
          .attr('y1', zeroY)
          .attr('y2', zeroY)
          .attr('stroke', '#9ca3af')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '4,3');
      }

      // Confidence band (shaded area)
      const bandData = periods.map((p, i) => ({
        period: p,
        upper: displayUpper[i] != null ? displayUpper[i] : values[i],
        lower: displayLower[i] != null ? displayLower[i] : values[i],
      }));

      const bandArea = d3.area()
        .x((d) => xScale(d.period))
        .y0((d) => yScale(d.lower))
        .y1((d) => yScale(d.upper))
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(bandData)
        .attr('d', bandArea)
        .attr('fill', '#9ca3af')
        .attr('fill-opacity', 0.15)
        .attr('stroke', 'none');

      // Response line — colored by sign at each segment
      const lineData = periods.map((p, i) => ({
        period: p,
        value: values[i],
      }));

      // Draw the line in segments colored by positive/negative
      for (let i = 0; i < lineData.length - 1; i++) {
        const curr = lineData[i];
        const next = lineData[i + 1];
        const avgVal = (curr.value + next.value) / 2;
        const segColor = avgVal >= 0 ? '#2563eb' : '#dc2626';

        g.append('line')
          .attr('x1', xScale(curr.period))
          .attr('y1', yScale(curr.value))
          .attr('x2', xScale(next.period))
          .attr('y2', yScale(next.value))
          .attr('stroke', segColor)
          .attr('stroke-width', 2)
          .attr('stroke-linecap', 'round');
      }

      // Dots
      lineData.forEach((d) => {
        const dotColor = d.value >= 0 ? '#2563eb' : '#dc2626';
        g.append('circle')
          .attr('cx', xScale(d.period))
          .attr('cy', yScale(d.value))
          .attr('r', 3)
          .attr('fill', dotColor)
          .attr('stroke', '#fff')
          .attr('stroke-width', 1);
      });

      // Half-life marker (only in impulse mode)
      if (viewMode === 'impulse' && resp.halfLife != null) {
        const hlX = xScale(resp.halfLife);
        const hlDomain = yScale.domain();
        if (resp.halfLife >= d3.min(periods) && resp.halfLife <= d3.max(periods)) {
          // Vertical marker line
          g.append('line')
            .attr('x1', hlX)
            .attr('x2', hlX)
            .attr('y1', 0)
            .attr('y2', innerH)
            .attr('stroke', '#f59e0b')
            .attr('stroke-width', 1)
            .attr('stroke-dasharray', '3,2');

          // Half-life label
          g.append('text')
            .attr('x', hlX)
            .attr('y', -4)
            .attr('text-anchor', 'middle')
            .attr('font-size', '9px')
            .attr('font-weight', 600)
            .attr('fill', '#f59e0b')
            .text(`t\u00BD=${resp.halfLife}`);
        }
      }

      // Title label for this mini-chart
      g.append('text')
        .attr('x', innerW / 2)
        .attr('y', -MARGIN.top + 14)
        .attr('text-anchor', 'middle')
        .attr('font-size', '11px')
        .attr('font-weight', 600)
        .attr('fill', '#374151')
        .text(`Response: ${fieldLabel(resp.responseField)}`);

      // X axis
      g.append('g')
        .attr('transform', `translate(0, ${innerH})`)
        .call(
          d3.axisBottom(xScale)
            .ticks(Math.min(periods.length, 7))
            .tickSize(0)
            .tickFormat((d) => `t${d}`)
        )
        .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
        .selectAll('text')
        .attr('font-size', '9px')
        .attr('fill', '#6b7280')
        .attr('dy', '0.8em');

      // Y axis
      g.append('g')
        .call(
          d3.axisLeft(yScale)
            .ticks(4)
            .tickSize(0)
            .tickFormat((d) => d3.format('.2f')(d))
        )
        .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
        .selectAll('text')
        .attr('font-size', '9px')
        .attr('fill', '#6b7280');

      g.selectAll('.tick line').attr('stroke', '#e5e7eb');

      // Invisible hover targets for each data point
      lineData.forEach((d, i) => {
        g.append('circle')
          .attr('cx', xScale(d.period))
          .attr('cy', yScale(d.value))
          .attr('r', 12)
          .attr('fill', 'transparent')
          .style('cursor', 'crosshair')
          .on('mousemove', (event) => {
            setTooltip({
              x: event.clientX + 12,
              y: event.clientY - 10,
              responseField: resp.responseField,
              period: d.period,
              value: d.value,
              upper: displayUpper[i] != null ? displayUpper[i] : null,
              lower: displayLower[i] != null ? displayLower[i] : null,
              cumulative: resp.cumulative ? resp.cumulative[i] : null,
              viewMode,
            });
          })
          .on('mouseleave', () => {
            setTooltip(null);
          });
      });
    });

    return () => {
      responses.forEach((resp) => {
        const svgEl = svgRefs.current[resp.responseField];
        if (svgEl) {
          d3.select(svgEl).selectAll('*').remove();
        }
      });
    };
  }, [responses, viewMode]);

  if (!data || !data.responses || data.responses.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Impulse Response Functions</div>
        <div style={styles.subtitle}>How a price shock propagates across fields</div>
        <div style={styles.empty}>No impulse response data available</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Impulse Response Functions</div>
          <div style={styles.subtitle}>How a price shock propagates across fields</div>
        </div>
        <div style={styles.buttonGroup}>
          <button
            style={{
              ...styles.button,
              ...styles.buttonLeft,
              ...(viewMode === 'impulse' ? styles.buttonActive : styles.buttonInactive),
            }}
            onClick={() => setViewMode('impulse')}
          >
            Impulse Response
          </button>
          <button
            style={{
              ...styles.button,
              ...styles.buttonRight,
              ...(viewMode === 'cumulative' ? styles.buttonActive : styles.buttonInactive),
            }}
            onClick={() => setViewMode('cumulative')}
          >
            Cumulative
          </button>
        </div>
      </div>

      <div style={styles.impulseLabel}>
        Shock: {fieldLabel(data.impulseField)}
      </div>

      <div style={styles.grid}>
        {responses.map((resp) => (
          <div key={resp.responseField} style={styles.cell}>
            <svg
              ref={(el) => {
                if (el) svgRefs.current[resp.responseField] = el;
              }}
              style={{ width: '100%', height: 'auto', display: 'block' }}
              viewBox={`0 0 ${MINI_WIDTH} ${MINI_HEIGHT}`}
              preserveAspectRatio="xMidYMid meet"
            />
          </div>
        ))}
      </div>

      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={styles.tooltipTitle}>
            {fieldLabel(tooltip.responseField)} at t={tooltip.period}
          </div>
          <div style={styles.tooltipRow}>
            <span>{tooltip.viewMode === 'cumulative' ? 'Cumulative' : 'Response'}</span>
            <span style={{ fontWeight: 600, color: tooltip.value >= 0 ? '#2563eb' : '#dc2626' }}>
              {tooltip.value != null ? tooltip.value.toFixed(4) : '--'}
            </span>
          </div>
          {tooltip.upper != null && tooltip.lower != null && (
            <div style={styles.tooltipRow}>
              <span>Confidence band</span>
              <span style={{ fontWeight: 600, color: '#374151' }}>
                [{tooltip.lower.toFixed(4)}, {tooltip.upper.toFixed(4)}]
              </span>
            </div>
          )}
          {tooltip.viewMode === 'impulse' && tooltip.cumulative != null && (
            <div style={styles.tooltipRow}>
              <span>Cumulative</span>
              <span style={{ fontWeight: 600, color: '#374151' }}>
                {tooltip.cumulative.toFixed(4)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
